import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Gift, MapPin, Package, Search, ArrowLeft, Heart, HandHeart } from "lucide-react";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicDonations() {
  const [donations, setDonations] = useState([]);
  const [city, setCity] = useState("");
  const [direction, setDirection] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (city) params.set("city", city);
        if (direction) params.set("direction", direction);
        const { data } = await axios.get(`${API}/public/donations?${params.toString()}`);
        setDonations(data);
      } catch {
        setDonations([]);
      }
      setLoading(false);
    };
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [city, direction]);

  return (
    <div className="min-h-screen bg-background" data-testid="public-donations-page">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-background/70 border-b border-border">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 h-14">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-block w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-heading font-black text-lg">R</span>
            </span>
            <span className="font-heading font-black">
              <span className="text-primary">Ri</span>vendas
            </span>
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
            <ArrowLeft size={14} /> Ir ao app
          </Link>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-4 pt-10 pb-6">
        <div className="flex items-center gap-2 mb-2">
          <Gift size={22} className="text-primary" />
          <p className="text-xs uppercase tracking-widest text-primary font-bold">Mural público de doações</p>
        </div>
        <h1 className="font-heading font-black text-4xl md:text-6xl tracking-tighter mb-3">
          Doações abertas no <span className="text-primary">Rivendas</span>
        </h1>
        <p className="text-muted-foreground max-w-2xl">
          Aqui você encontra pessoas dispostas a doar ou que precisam de ajuda.
          Cadastre-se para conversar direto com quem publicou — 100% grátis.
        </p>

        <div className="flex flex-col sm:flex-row gap-2 mt-6">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="donations-city-filter"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Filtrar por cidade (ex: São Paulo)"
              className="pl-9 h-11"
            />
          </div>
          <div className="grid grid-cols-3 gap-1 bg-secondary p-1 rounded-xl">
            {[
              { v: "", l: "Todas", i: <Gift size={14} /> },
              { v: "dar", l: "Ofereço", i: <Heart size={14} /> },
              { v: "receber", l: "Preciso", i: <HandHeart size={14} /> },
            ].map((d) => (
              <button
                key={d.v || "all"}
                data-testid={`filter-${d.v || "all"}`}
                onClick={() => setDirection(d.v)}
                className={`h-9 px-3 rounded-lg text-xs font-medium flex items-center gap-1 ${
                  direction === d.v ? "bg-background shadow-sm" : "text-muted-foreground"
                }`}
              >
                {d.i} {d.l}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 pb-16">
        {loading ? (
          <p className="text-center text-muted-foreground py-16">Carregando doações...</p>
        ) : donations.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-border rounded-2xl">
            <Gift size={40} className="mx-auto text-muted-foreground mb-3 opacity-50" />
            <p className="text-muted-foreground">Nenhuma doação disponível com esses filtros</p>
            <Link to="/register" className="text-primary font-bold underline mt-3 inline-block">
              Seja o primeiro a publicar →
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {donations.map((d) => (
              <div
                key={d.order_id}
                data-testid={`donation-${d.order_id}`}
                className="bg-card border border-border rounded-2xl p-5 hover:border-primary/50 transition-colors"
              >
                <div className="flex items-start justify-between gap-2 mb-3">
                  <span
                    className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full border ${
                      d.donation_direction === "dar"
                        ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/40"
                        : "bg-pink-500/20 text-pink-400 border-pink-500/40"
                    }`}
                  >
                    {d.donation_direction === "dar" ? "❤️ Ofereço" : "🙏 Preciso"}
                  </span>
                  {d.category && (
                    <span className="text-[10px] text-muted-foreground">{d.category}</span>
                  )}
                </div>
                <h3 className="font-heading font-bold text-lg mb-2 leading-tight">{d.title}</h3>
                {d.description && (
                  <p className="text-sm text-muted-foreground line-clamp-3 mb-3">{d.description}</p>
                )}
                <div className="flex items-center gap-3 text-xs text-muted-foreground border-t border-border pt-3">
                  <span className="flex items-center gap-1"><MapPin size={12} /> {d.city}</span>
                  <span className="flex items-center gap-1"><Package size={12} /> {d.quantity}× {d.condition}</span>
                </div>
                <Link
                  to="/register?role=buyer"
                  className="mt-3 block text-center text-sm text-primary font-bold hover:underline"
                  data-testid={`donation-cta-${d.order_id}`}
                >
                  Entrar para conversar →
                </Link>
              </div>
            ))}
          </div>
        )}
      </section>

      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-4 py-8 text-sm text-muted-foreground text-center">
          © 2026 Rivendas · Marketplace reverso brasileiro
        </div>
      </footer>
    </div>
  );
}
